import React, { useState, useEffect } from 'react';
import { startReport, getReport } from '../services/data/reportService';
import { useNavigate } from 'react-router-dom';

export default function CreateReport(){
  const [step, setStep] = useState(0); // 0 form, 1 review, 2 generating
  const [reportId, setReportId] = useState(null);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({
    timeframe: '30d',
    format: 'pdf',
    sections: ['esg','risk','optimization'],
    includeBenchmarks: true
  });
  const navigate = useNavigate();

  useEffect(()=>{
    let t;
    if(reportId){
      const poll = async ()=>{
        const r = await getReport(reportId);
        setReport(r ? { ...r } : null);
        if(r && r.status === 'generating') t = setTimeout(poll, 1700);
      };
      poll();
    }
    return ()=> clearTimeout(t);
  }, [reportId]);

  const toggleSection = (key)=>{
    setForm(f=> ({...f, sections: f.sections.includes(key) ? f.sections.filter(s=> s!==key) : [...f.sections, key]}));
  };

  const launch = async ()=>{
    const { id } = await startReport(form);
    setReportId(id);
    setStep(2);
  };

  const reset = ()=>{
    setStep(0); setReportId(null); setReport(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Create Report</h1>
        <p className="text-slate-600 text-sm mt-1">Build a custom Trust Tech report with selected sections and timeframe.</p>
      </header>
      {step === 0 && <FormStep form={form} setForm={setForm} toggleSection={toggleSection} onNext={()=> setStep(1)} onCancel={()=> navigate(-1)} />}
      {step === 1 && <ReviewStep form={form} onBack={()=> setStep(0)} onLaunch={launch} />}
      {step === 2 && <GeneratingStep report={report} reportId={reportId} onReset={reset} />}
    </div>
  );
}

function SectionWrapper({children}){ return <section className="bg-white border rounded-xl p-6 space-y-6 shadow-sm">{children}</section>; }

function FormStep({form, setForm, toggleSection, onNext, onCancel}){
  const formats = ['pdf','csv','json'];
  const sectionDefs = [
    {key:'esg', label:'ESG Summary'},
    {key:'risk', label:'Risk Radar'},
    {key:'optimization', label:'Optimization Opportunities'},
    {key:'suppliers', label:'Supplier Metrics'},
  ];
  return (
    <SectionWrapper>
      <div>
        <label className="font-medium text-sm">Timeframe</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {['30d','60d','90d','180d'].map(tf=> (
            <button key={tf} onClick={()=> setForm(f=>({...f, timeframe:tf}))} className={`px-3 py-1.5 rounded-md text-sm border ${form.timeframe===tf ? 'bg-purple-600 text-white border-purple-600':'bg-white hover:bg-slate-50 border-slate-300'}`}>{tf}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="font-medium text-sm">Sections</label>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {sectionDefs.map(s=> (
            <label key={s.key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.sections.includes(s.key)} onChange={()=> toggleSection(s.key)} />
              {s.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="font-medium text-sm">Format</label>
        <div className="mt-2 flex gap-2">
          {formats.map(f=> (
            <button key={f} onClick={()=> setForm(frm=>({...frm, format:f}))} className={`px-3 py-1.5 rounded-md text-sm border capitalize ${form.format===f ? 'bg-purple-600 text-white border-purple-600':'bg-white hover:bg-slate-50 border-slate-300'}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input id="benchmarks" type="checkbox" checked={form.includeBenchmarks} onChange={e=> setForm(f=>({...f, includeBenchmarks:e.target.checked}))} />
        <label htmlFor="benchmarks" className="text-sm">Include industry benchmarks</label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-md border text-sm bg-white hover:bg-slate-50">Cancel</button>
        <button onClick={onNext} className="px-4 py-2 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-500">Next</button>
      </div>
    </SectionWrapper>
  );
}

function ReviewStep({form, onBack, onLaunch}){
  return (
    <SectionWrapper>
      <h2 className="font-medium text-lg">Review & Generate</h2>
      <div className="text-sm space-y-1">
        <Row label="Timeframe" value={form.timeframe} />
        <Row label="Sections" value={form.sections.length} />
        <Row label="Format" value={form.format.toUpperCase()} />
        <Row label="Benchmarks" value={form.includeBenchmarks? 'Yes':'No'} />
      </div>
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="px-4 py-2 rounded-md border text-sm bg-white hover:bg-slate-50">Back</button>
        <button onClick={onLaunch} className="px-4 py-2 rounded-md text-sm bg-green-600 text-white hover:bg-green-500">Generate Report</button>
      </div>
    </SectionWrapper>
  );
}

function GeneratingStep({report, reportId, onReset}){
  return (
    <SectionWrapper>
      <h2 className="font-medium text-lg">Report Generation</h2>
      <div className="text-xs text-slate-500">ID: {reportId}</div>
      <div className="h-3 w-full bg-slate-200 rounded overflow-hidden mt-2">
        <div className="h-full bg-purple-600 transition-all" style={{width:`${report?.progress||0}%`}} />
      </div>
      <div className="mt-1 text-xs text-slate-600">
        {report?.status === 'ready' ? 'Ready' : `Generating… ${Math.floor(report?.progress||0)}%`}
      </div>
      {report?.status === 'ready' && (
        <div className="bg-slate-50 border rounded-lg p-4 text-sm space-y-3 mt-4">
          <div className="font-medium">Summary</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Row label="Timeframe" value={report.summary.timeframe} />
            <Row label="Sections" value={report.summary.sections} />
            <Row label="Format" value={report.summary.format.toUpperCase()} />
            <Row label="Issues" value={report.summary.issues} />
            <Row label="Optimizations" value={report.summary.optimizations} />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {Object.entries(report.downloadUrls).map(([k,v])=> (
              <a key={k} href={v} className="px-3 py-2 rounded-md text-xs bg-purple-600 text-white hover:bg-purple-500" download>{k.toUpperCase()}</a>
            ))}
            <button onClick={onReset} className="px-3 py-2 rounded-md border text-xs bg-white hover:bg-slate-50">New Report</button>
          </div>
        </div>
      )}
    </SectionWrapper>
  );
}

function Row({label, value}){ return <div className="flex gap-1"><span className="font-semibold">{label}:</span><span>{value}</span></div>; }
